<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import Select from 'primevue/select'
import D3DonutChart from '@/components/charts/D3DonutChart.vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { regionNameFromISO2, normalizeISO2 } from '@/utils/country'

const { t, activeLang } = useI18n()

const props = defineProps({
  activeFilters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['filter'])

const loading = ref(true)
const error   = ref('')
const stats   = ref(null)
const days    = ref(30)

const daysOptions = computed(() => [
  { label: t('news.list.statsDays7'),   value: 7 },
  { label: t('news.list.statsDays30'),  value: 30 },
  { label: t('news.list.statsDays90'),  value: 90 },
  { label: t('news.list.statsDays365'), value: 365 },
])

async function fetchStats() {
  loading.value = true
  error.value   = ''
  try {
    const { data: res } = await api.getNewsStats({ days: days.value })
    stats.value = res?.data ?? null
  } catch {
    error.value = t('news.list.statsLoadFailed')
  } finally {
    loading.value = false
  }
}

watch(days, () => fetchStats())

const countryLabel = (code) => {
  const c = normalizeISO2(code) || code
  return regionNameFromISO2(c, [activeLang.value]) || c
}

const countriesData = computed(() => (stats.value?.countries ?? []).map(r => ({
  label: r.name ? countryLabel(r.name) : t('news.list.statsUnknown'),
  value: r.count,
  raw:   r.name ?? null,
})))

const sourcesData = computed(() => (stats.value?.sources ?? []).map(r => ({
  label: r.name,
  value: r.count,
  raw:   r.name,
})))

function onSliceClick(dimension, item) {
  if (item.isOther || item.raw == null) return
  emit('filter', { dimension, value: item.raw })
}

onMounted(fetchStats)
defineExpose({ refresh: fetchStats })
</script>

<template>
  <div class="news-stats-panel">

    <!-- ── Top Länder ─────────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-flag hw-card-icon" />
        <span class="hw-card-title">{{ t('news.list.statsCountries') }}</span>
        <Select
          v-model="days"
          :options="daysOptions"
          option-label="label"
          option-value="value"
          size="small"
          class="nsp-days-select"
        />
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="countriesData"
          :other-label="t('news.list.statsOther')"
          :selected="props.activeFilters?.country ?? null"
          @slice-click="item => onSliceClick('country', item)"
        />
      </div>
    </div>

    <!-- ── Top Quellen ────────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-rss hw-card-icon" />
        <span class="hw-card-title">{{ t('news.list.statsSources') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="sourcesData"
          :other-label="t('news.list.statsOther')"
          :selected="props.activeFilters?.source ?? null"
          @slice-click="item => onSliceClick('source', item)"
        />
      </div>
    </div>

    <div v-if="!loading && error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.news-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.nsp-days-select {
  font-size: .72rem;
}
</style>
